/**
 * Plays composed tracks on a WebAudio context: a deck per playing track, scheduled a little ahead
 * in small steps, crossfading from one track to the next and ducking under stingers. Works the same
 * on an OfflineAudioContext (offline.ts renders the review files with it).
 *
 * Cost is kept low for the game running alongside: every sound is a recorded-once sample or a
 * single oscillator (two or three nodes a note), a held phrase is one tone gliding from note to
 * note, a restruck string cuts its last note off, and no more than `maxVoices` sound at once.
 * The nodes alive are counted (`stats()`), so a leak shows.
 */
import { compileBody, compileTrack, type CompiledBlock, type MusicEvent } from './compile';
import { SampleBank, type HeldKind } from './bank';
import { mtof, pentatonicStep } from './theory';
import { INSTRUMENTS, type PartDef, type TrackDef } from './types';

/** Seconds scheduled ahead of the clock. */
const LOOKAHEAD = 0.35;

interface Voice {
  readonly nodes: AudioNode[];
  readonly gain: GainNode;
  readonly sources: AudioScheduledSourceNode[];
  readonly start: number;
  end: number;
  readonly pluck: boolean;
  alive: boolean;
}

interface Channel {
  readonly input: GainNode;
  readonly nodes: AudioNode[];
}

/** Base levels of the instruments, so a part at gain 1 sits right in the band. */
const LEVEL = { zheng: 0.5, qin: 0.62, pipa: 0.42, dizi: 0.2, xiao: 0.24, erhu: 0.17, pad: 0.07, perc: 0.5 };

export interface MusicStats {
  /** Audio nodes alive now (the engine's own, the decks' channels and every sounding voice). */
  readonly nodes: number;
  readonly voices: number;
  readonly decks: number;
  readonly track: string | null;
}

export class MusicEngine {
  readonly bank: SampleBank;
  /** Everything ends here: master → compressor → out. */
  private readonly master: GainNode;
  /** The looping music (ducked under stingers). */
  private readonly bgm: GainNode;
  /** Send to the hall. */
  readonly hallIn: GainNode;
  private readonly own: AudioNode[];
  private readonly decks: Deck[] = [];
  private current: Deck | null = null;
  /** Where each track was left, to pick it up again after a short detour (a fight, a meditation). */
  private readonly left = new Map<string, { pos: number; at: number }>();
  /** Nodes alive in voices and channels. */
  nodes = 0;
  readonly voices: Voice[] = [];
  /** Playing in real time (not rendering offline). */
  private readonly live: boolean;

  constructor(
    readonly ctx: BaseAudioContext,
    out: AudioNode,
    readonly maxVoices = 40,
  ) {
    this.bank = new SampleBank(ctx);
    this.live = !(typeof OfflineAudioContext !== 'undefined' && ctx instanceof OfflineAudioContext);
    this.master = ctx.createGain();
    this.master.gain.value = 0.85;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 10;
    comp.ratio.value = 3;
    comp.attack.value = 0.01;
    comp.release.value = 0.35;
    // A gentle shelf takes the digital edge off the top.
    const shelf = ctx.createBiquadFilter();
    shelf.type = 'highshelf';
    shelf.frequency.value = 6500;
    shelf.gain.value = -4;
    this.master.connect(shelf).connect(comp).connect(out);
    this.bgm = ctx.createGain();
    this.bgm.connect(this.master);
    this.hallIn = ctx.createGain();
    const verb = ctx.createConvolver();
    verb.buffer = this.bank.hall();
    const wet = ctx.createGain();
    wet.gain.value = 0.9;
    this.hallIn.connect(verb).connect(wet).connect(this.master);
    this.own = [this.master, shelf, comp, this.bgm, this.hallIn, verb, wet];
  }

  /** The id of the track playing (or fading in), if any. */
  get playing(): string | null {
    return this.current?.def.id ?? null;
  }

  /**
   * Crossfades to a track (null: to silence). A track left less than two and a half minutes ago
   * picks up at the start of the section it was in.
   */
  play(def: TrackDef | null, fade = 2.5, at = this.ctx.currentTime): void {
    if ((def?.id ?? null) === this.playing) return;
    const old = this.current;
    if (old) {
      this.left.set(old.def.id, { pos: old.position(at), at });
      old.fadeOut(at, fade);
    }
    this.current = null;
    if (!def) return;
    const was = this.left.get(def.id);
    const resume = was && at - was.at < 150 ? was.pos : null;
    this.prepare(def, true);
    // A moment's grace before it starts, so its sounds are made in idle slices, not all at once.
    const d = new Deck(this, def, this.bgm, at + (this.live ? 1.2 : 0.05), resume, old ? fade : 0.4);
    this.decks.push(d);
    this.current = d;
  }

  /** Makes the sounds a track uses ahead of time, a few each tick (a track likely to come next). */
  prepare(def: TrackDef, first = false): void {
    const c = compileTrack(def);
    this.bank.want([...(c.intro?.events ?? []), ...c.body.events], first);
  }

  /** Plays a stinger over the music, ducking it meanwhile. Returns its length in seconds. */
  sting(def: TrackDef, at = this.ctx.currentTime): number {
    const d = new Deck(this, def, this.master, at + 0.05, null, 0);
    this.decks.push(d);
    const len = d.length;
    const g = this.bgm.gain;
    g.cancelScheduledValues(at);
    g.setTargetAtTime(def.id === 'death' ? 0.08 : 0.3, at, 0.12);
    g.setTargetAtTime(1, at + len, 1.2);
    return len;
  }

  /** Schedules everything due before `until` (default: a little ahead of the clock), and tidies up. */
  tick(until = this.ctx.currentTime + LOOKAHEAD): void {
    const now = this.ctx.currentTime;
    if (this.live) this.bank.idle(6);
    for (const d of this.decks) d.schedule(until);
    for (let i = this.decks.length - 1; i >= 0; i--) {
      const d = this.decks[i]!;
      if (d.doneBy !== null && now > d.doneBy) {
        d.dispose();
        this.decks.splice(i, 1);
      }
    }
    // Voices whose end has passed (their `ended` may come late or, offline, not at all).
    for (let i = this.voices.length - 1; i >= 0; i--) if (this.voices[i]!.end < now - 0.5) this.release(this.voices[i]!);
  }

  stats(): MusicStats {
    return {
      nodes: this.own.length + this.nodes,
      voices: this.voices.length,
      decks: this.decks.length,
      track: this.playing,
    };
  }

  /** Stops everything at once. */
  dispose(): void {
    for (const d of this.decks) d.dispose();
    this.decks.length = 0;
    this.current = null;
    for (const n of this.own) n.disconnect();
  }

  // ───────── voices ─────────

  /** Registers a voice's nodes; they are let go when its source ends. */
  voice(v: Omit<Voice, 'alive'>): Voice {
    const voice: Voice = { ...v, alive: true };
    this.nodes += voice.nodes.length;
    this.voices.push(voice);
    voice.sources[0]!.onended = () => this.release(voice);
    return voice;
  }

  release(v: Voice): void {
    if (!v.alive) return;
    v.alive = false;
    this.nodes -= v.nodes.length;
    for (const n of v.nodes) n.disconnect();
    const i = this.voices.indexOf(v);
    if (i >= 0) this.voices.splice(i, 1);
  }

  /** Cuts a voice off at `t` with a quick fade (a restruck string, a stolen voice, a deck gone). */
  cut(v: Voice, t: number): void {
    if (v.end <= t) return;
    const g = v.gain.gain;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(0, t, 0.015);
    for (const s of v.sources) s.stop(t + 0.1);
    v.end = t + 0.1;
  }

  /** Makes room for a pluck at `t`: drops voices already over, and cuts the oldest pluck if full. */
  room(t: number): void {
    let sounding = 0;
    for (const v of this.voices) if (v.start <= t && v.end > t) sounding++;
    if (sounding < this.maxVoices) return;
    const oldest = this.voices.find((v) => v.pluck && v.start <= t && v.end > t);
    if (oldest) this.cut(oldest, t);
  }
}

/** One track playing: its channels, its place in the score, and its own fade. */
class Deck {
  readonly out: GainNode;
  private readonly channels = new Map<string, Channel>();
  private block: CompiledBlock;
  private inIntro: boolean;
  private pass = 0;
  /** Context time the current block started at. */
  private t0: number;
  private idx = 0;
  /** Held phrases already scheduled (by their first note), and the notes they took. */
  private taken = new Set<MusicEvent>();
  /** The last pluck on each string, cut when it is struck again. */
  private readonly strings = new Map<string, Voice>();
  private readonly mine = new Set<Voice>();
  /** When the deck can be taken down (after its fade-out, or after a stinger's end). */
  doneBy: number | null = null;
  private stopAt = Infinity;
  /** Length of a stinger (intro + one pass). */
  readonly length: number;

  constructor(
    private readonly eng: MusicEngine,
    readonly def: TrackDef,
    dest: AudioNode,
    start: number,
    resume: number | null,
    fadeIn: number,
  ) {
    const ctx = eng.ctx;
    this.out = ctx.createGain();
    this.out.gain.value = fadeIn > 0 ? 0 : (def.gain ?? 1);
    if (fadeIn > 0) this.out.gain.setTargetAtTime(def.gain ?? 1, start, fadeIn / 4);
    this.out.connect(dest);
    eng.nodes++;
    const c = compileTrack(def, 0);
    this.length = (c.intro?.dur ?? 0) + c.body.dur;
    if (resume !== null && resume > 0) {
      // Pick up at the start of the section the track was left in.
      const sec = [...c.body.sections].reverse().find((s) => s.t <= resume % c.body.dur + 1e-6);
      const off = sec?.t ?? 0;
      this.block = c.body;
      this.inIntro = false;
      this.t0 = start - off;
      this.idx = c.body.events.findIndex((e) => e.t >= off - 1e-6);
      if (this.idx < 0) this.idx = c.body.events.length;
    } else {
      this.inIntro = !!c.intro;
      this.block = c.intro ?? c.body;
      this.t0 = start;
    }
    if (!def.loop) this.doneBy = start + this.length + 6;
  }

  /** Seconds into the loop at context time `t` (0 while in the intro). */
  position(t: number): number {
    return this.inIntro ? 0 : Math.max(0, t - this.t0);
  }

  fadeOut(at: number, fade: number): void {
    const g = this.out.gain;
    g.cancelScheduledValues(at);
    g.setTargetAtTime(0, at, fade / 4);
    this.stopAt = at + fade * 1.5;
    this.doneBy = Math.min(this.doneBy ?? Infinity, this.stopAt + 0.2);
  }

  schedule(until: number): void {
    const end = Math.min(until, this.stopAt);
    for (;;) {
      const evs = this.block.events;
      while (this.idx < evs.length && this.t0 + evs[this.idx]!.t < end) {
        const e = evs[this.idx++]!;
        if (this.taken.has(e)) continue;
        this.play(e, this.t0 + e.t, evs);
      }
      if (this.idx < evs.length) return;
      // The block is over once its full length has passed the scheduling edge.
      const next = this.t0 + this.block.dur;
      if (next >= end) return;
      if (!this.inIntro && !this.def.loop) {
        this.stopAt = next;
        return;
      }
      if (this.inIntro) this.inIntro = false;
      else this.pass++;
      this.block = compileBody(this.def, this.pass);
      this.t0 = next;
      this.idx = 0;
      this.taken = new Set();
    }
  }

  dispose(): void {
    const now = this.eng.ctx.currentTime;
    for (const v of this.mine) {
      if (!v.alive) continue;
      for (const s of v.sources)
        try {
          s.stop(now);
        } catch {
          /* already stopped */
        }
      this.eng.release(v);
    }
    this.mine.clear();
    for (const ch of this.channels.values()) {
      for (const n of ch.nodes) n.disconnect();
      this.eng.nodes -= ch.nodes.length;
    }
    this.channels.clear();
    this.out.disconnect();
    this.eng.nodes--;
  }

  private channel(part: string): Channel {
    let ch = this.channels.get(part);
    if (ch) return ch;
    const ctx = this.eng.ctx;
    const pd: PartDef = this.def.parts[part]!;
    const input = ctx.createGain();
    input.gain.value = (pd.gain ?? 1) * LEVEL[pd.inst];
    const nodes: AudioNode[] = [input];
    let last: AudioNode = input;
    if (pd.inst === 'pad' || pd.inst === 'erhu') {
      const f = ctx.createBiquadFilter();
      if (pd.inst === 'pad') {
        f.type = 'lowpass';
        f.frequency.value = 1100;
      } else {
        f.type = 'peaking';
        f.frequency.value = 1400;
        f.Q.value = 1.2;
        f.gain.value = 4;
      }
      last.connect(f);
      nodes.push(f);
      last = f;
    }
    const pan = ctx.createStereoPanner();
    pan.pan.value = pd.pan ?? 0;
    last.connect(pan).connect(this.out);
    const send = ctx.createGain();
    send.gain.value = pd.reverb ?? 0.3;
    last.connect(send).connect(this.eng.hallIn);
    nodes.push(pan, send);
    ch = { input, nodes };
    this.channels.set(part, ch);
    this.eng.nodes += nodes.length;
    return ch;
  }

  private play(e: MusicEvent, when: number, evs: readonly MusicEvent[]): void {
    const kind = INSTRUMENTS[e.inst].kind;
    if (kind === 'pluck') this.pluck(e, when);
    else if (kind === 'perc') this.hit(e, when);
    else if (kind === 'pad') this.pad(e, when);
    else this.phrase(e, when, evs);
  }

  private track(v: Omit<Voice, 'alive'>): Voice {
    const voice = this.eng.voice(v);
    this.mine.add(voice);
    const done = voice.sources[0]!.onended;
    voice.sources[0]!.onended = (ev) => {
      this.mine.delete(voice);
      done?.call(voice.sources[0]!, ev);
    };
    return voice;
  }

  // ───────── plucked strings ─────────

  private pluck(e: MusicEvent, when: number): void {
    const ctx = this.eng.ctx;
    const inst = e.inst as 'zheng' | 'qin' | 'pipa';
    const harm = e.techs.includes('fan');
    const key = `${e.part}:${e.midi}`;
    const prev = this.strings.get(key);
    if (prev?.alive) this.eng.cut(prev, when);
    this.eng.room(when);
    const s = this.eng.bank.pluck(inst, e.midi, harm);
    const src = ctx.createBufferSource();
    src.buffer = s.buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(e.vel * (harm ? 0.75 : 1), when);
    const ring = Math.max(0.1, e.ring ?? e.dur + 1);
    const natural = s.buffer.duration / s.rate;
    const end = when + Math.min(natural, ring + 0.5);
    if (ring < natural) g.gain.setTargetAtTime(0, when + ring, 0.07);
    this.bend(src.playbackRate, s.rate, e, when, Math.min(natural, ring), s.recorded);
    // The recording has one (firm) pluck per string: a softer finger is darker, not only quieter.
    const soft = s.recorded ? softCutoff(e.vel, e.midi) : 0;
    const nodes: AudioNode[] = [src, g];
    if (soft) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = soft;
      lp.Q.value = 0.5;
      src.connect(lp).connect(g);
      nodes.push(lp);
    } else src.connect(g);
    g.connect(this.channel(e.part).input);
    src.start(when);
    src.stop(end);
    const v = this.track({ nodes, gain: g, sources: [src], start: when, end, pluck: true });
    this.strings.set(key, v);
  }

  /**
   * The left hand on the string: 按 pressing up into the note, 绰 sliding up into it, 注 falling off
   * it, 揉/吟 vibrato, and (on the zheng and pipa) the slight sharpness of a fresh pluck settling.
   */
  private bend(param: AudioParam, rate: number, e: MusicEvent, when: number, span: number, recorded = false): void {
    const qin = e.inst === 'qin';
    const has = (t: string) => e.techs.includes(t as never);
    // A recorded string already settles from its own sharp attack.
    const fresh = e.inst !== 'qin' && !has('fan') && !recorded;
    if (!fresh && !e.techs.some((t) => t === 'an' || t === 'chuo' || t === 'zhu' || t === 'rou')) {
      param.setValueAtTime(rate, when);
      return;
    }
    const key = this.def.key;
    const below = (e.midi - pentatonicStep(key, e.midi, -1)) * 100;
    const len = Math.max(0.05, Math.min(span, e.dur + (qin ? 2 : 1.2)));
    const n = Math.max(4, Math.ceil(len * 90));
    const curve = new Float32Array(n);
    const rouDepth = qin ? 22 : 30;
    const rouRate = qin ? 4.2 : 5.6;
    for (let i = 0; i < n; i++) {
      const t = (i / (n - 1)) * len;
      let cents = 0;
      if (fresh) cents += 7 * Math.exp(-t / 0.03);
      if (has('an')) cents -= below * Math.max(0, 1 - t / (qin ? 0.16 : 0.1));
      if (has('chuo')) {
        const k = Math.min(1, t / (qin ? 0.28 : 0.18));
        cents -= below * (1 - k) ** 2;
      }
      if (has('zhu') && t > e.dur * 0.6) {
        const k = Math.min(1, (t - e.dur * 0.6) / Math.max(0.12, e.dur * 0.35));
        cents -= (qin ? 200 : below) * k * k;
      }
      if (has('rou') && t > 0.12) {
        const grow = Math.min(1, (t - 0.12) / 0.3);
        cents += rouDepth * grow * Math.sin(2 * Math.PI * rouRate * (t - 0.12));
      }
      curve[i] = rate * 2 ** (cents / 1200);
    }
    param.setValueCurveAtTime(curve, when, len);
  }

  // ───────── drums ─────────

  private hit(e: MusicEvent, when: number): void {
    const ctx = this.eng.ctx;
    const buf = this.eng.bank.drum(e.drum!);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = e.vel;
    src.connect(g).connect(this.channel(e.part).input);
    src.start(when);
    const end = when + buf.duration;
    src.stop(end);
    this.track({ nodes: [src, g], gain: g, sources: [src], start: when, end, pluck: false });
  }

  // ───────── the pad ─────────

  private pad(e: MusicEvent, when: number): void {
    const ctx = this.eng.ctx;
    const o = ctx.createOscillator();
    o.setPeriodicWave(this.eng.bank.wave('pad'));
    o.frequency.value = mtof(e.midi);
    const g = ctx.createGain();
    const gg = g.gain;
    gg.setValueAtTime(0, when);
    gg.setTargetAtTime(e.vel, when, Math.min(0.6, e.dur / 5));
    gg.setTargetAtTime(0, when + Math.max(0.1, e.dur - 0.3), 0.45);
    o.connect(g).connect(this.channel(e.part).input);
    o.start(when);
    const end = when + e.dur + 2.6;
    o.stop(end);
    this.track({ nodes: [o, g], gain: g, sources: [o], start: when, end, pluck: false });
  }

  // ───────── held phrases: dizi, xiao, erhu ─────────

  private phrase(first: MusicEvent, when: number, evs: readonly MusicEvent[]): void {
    const notes = evs.filter((e) => e.part === first.part && e.phrase === first.phrase);
    for (const n of notes) this.taken.add(n);
    const ctx = this.eng.ctx;
    const kind = first.inst as HeldKind;
    const base = when - first.t;
    const start = when;
    const last = notes[notes.length - 1]!;
    const end = base + last.t + last.dur;
    const o = ctx.createOscillator();
    o.setPeriodicWave(this.eng.bank.wave(kind));
    const g = ctx.createGain();
    const erhu = kind === 'erhu';
    const glide = erhu ? 0.09 : kind === 'xiao' ? 0.04 : 0.025;
    const att = erhu ? 0.12 : kind === 'xiao' ? 0.1 : 0.05;
    const f = o.frequency;
    const gg = g.gain;
    gg.setValueAtTime(0, start);
    let prevMidi = first.midi;
    notes.forEach((n, i) => {
      const t = base + n.t;
      const level = n.vel;
      if (i === 0) {
        f.setValueAtTime(mtof(n.midi), t);
        gg.setTargetAtTime(level, t, att / 3);
      } else {
        // Slide (erhu) or step (flutes) to the new note, with a little lift of the bow or tongue.
        f.setValueAtTime(mtof(prevMidi), t);
        const slow = n.techs.includes('chuo') || n.techs.includes('an') ? 2.5 : 1;
        f.exponentialRampToValueAtTime(mtof(n.midi), t + Math.min(glide * slow, n.dur * 0.5));
        gg.setTargetAtTime(level * (erhu ? 0.65 : 0.45), t - 0.02, 0.012);
        gg.setTargetAtTime(level, t + 0.012, att / 4);
      }
      if (n.dur > 1.1) {
        gg.setTargetAtTime(level * 1.12, t + n.dur * 0.35, n.dur * 0.15);
        gg.setTargetAtTime(level * 0.88, t + n.dur * 0.75, n.dur * 0.08);
      }
      prevMidi = n.midi;
    });
    gg.setTargetAtTime(0, end - 0.04, erhu ? 0.09 : 0.05);
    // Vibrato and the falls and slides of the left hand, as one curve over the phrase.
    const len = end - start + 0.1;
    const steps = Math.max(4, Math.ceil(len * 60));
    const curve = new Float32Array(steps);
    const depth = erhu ? 16 : kind === 'xiao' ? 9 : 11;
    const rate = erhu ? 5.8 : kind === 'xiao' ? 4.6 : 5.3;
    for (let i = 0; i < steps; i++) {
      const t = start + (i / (steps - 1)) * len;
      const n = notes.findLast((x) => base + x.t <= t + 1e-6) ?? first;
      const into = t - (base + n.t);
      let cents = 0;
      const delay = n.techs.includes('rou') ? 0.08 : 0.28;
      if (into > delay && n.dur > 0.35) {
        const grow = Math.min(1, (into - delay) / 0.35);
        cents += depth * (n.techs.includes('rou') ? 1.8 : 1) * grow * Math.sin(2 * Math.PI * rate * into);
      }
      if (n.techs.includes('zhu') && into > n.dur * 0.6) cents -= 160 * Math.min(1, (into - n.dur * 0.6) / (n.dur * 0.38));
      curve[i] = cents;
    }
    o.detune.setValueCurveAtTime(curve, start, len);
    o.connect(g).connect(this.channel(first.part).input);
    o.start(start);
    const stop = end + 0.5;
    o.stop(stop);
    const nodes: AudioNode[] = [o, g];
    const sources: AudioScheduledSourceNode[] = [o];
    if (!erhu) {
      // Breath across the embouchure: a soft hiss, a puff at each note.
      const ns = ctx.createBufferSource();
      ns.buffer = this.eng.bank.noise();
      ns.loop = true;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = Math.min(6000, mtof(first.midi) * (kind === 'xiao' ? 2 : 3));
      bp.Q.value = 1.4;
      const bg = ctx.createGain();
      const b = bg.gain;
      const amt = kind === 'xiao' ? 0.5 : 0.32;
      b.setValueAtTime(0, start);
      for (const n of notes) {
        const t = base + n.t;
        b.setTargetAtTime(n.vel * amt * 1.8, t, 0.006);
        b.setTargetAtTime(n.vel * amt * 0.45, t + 0.03, 0.05);
      }
      b.setTargetAtTime(0, end - 0.04, 0.05);
      ns.connect(bp).connect(bg).connect(this.channel(first.part).input);
      ns.start(start, ((first.midi * 0.137) % 2.5) + 0.01);
      ns.stop(stop);
      nodes.push(ns, bp, bg);
      sources.push(ns);
    }
    this.track({ nodes, gain: g, sources, start, end: stop, pluck: false });
  }
}

/**
 * Low-pass cutoff for a soft pluck of the recorded zheng (0: loud enough to play as recorded). It opens
 * with the velocity (p ≈ 3.6 kHz, mp ≈ 5 kHz, mf ≈ 7 kHz) and stays above the note's 6th partial.
 */
export function softCutoff(vel: number, midi: number): number {
  const f = 900 * 2 ** (vel * 4);
  if (f >= 9000) return 0;
  return Math.max(f, 6 * 440 * 2 ** ((midi - 69) / 12));
}
